<script setup lang="ts">
/**
 * Champ texte sur mesure. Fond `surface`, sans bordure ; anneau bleu au focus, rouge en erreur.
 * Le type `password` ajoute un bouton afficher / masquer.
 */
import { computed, ref, useId } from 'vue'

import { fr } from '@/copy/fr'

import type { IconName } from '../icons/registry'
import Field from './Field.vue'
import Icon from './Icon.vue'

const props = withDefaults(
  defineProps<{
    label: string
    type?: 'text' | 'email' | 'password' | 'search'
    placeholder?: string
    hint?: string
    error?: string
    icon?: IconName
    autocomplete?: string
    inputmode?: 'text' | 'email' | 'numeric' | 'search'
    maxlength?: number
    disabled?: boolean
  }>(),
  {
    type: 'text',
    placeholder: undefined,
    hint: undefined,
    error: undefined,
    icon: undefined,
    autocomplete: undefined,
    inputmode: undefined,
    maxlength: undefined,
    disabled: false,
  },
)

const model = defineModel<string>({ default: '' })
const id = useId()
const revealed = ref(false)

const inputType = computed(() =>
  props.type === 'password' ? (revealed.value ? 'text' : 'password') : props.type,
)
</script>

<template>
  <Field :id="id" :label="label" :hint="hint" :error="error">
    <template #default="{ describedBy }">
      <div
        :class="[
          'flex h-control items-center gap-3 rounded-control bg-surface px-4 transition-colors duration-80',
          'focus-within:outline-2 focus-within:outline-offset-2',
          error
            ? 'outline-2 outline-danger focus-within:outline-danger'
            : 'focus-within:outline-accent',
          disabled ? 'text-ink-disabled' : 'text-ink',
        ]"
      >
        <Icon v-if="icon" :name="icon" class="text-ink-muted" />
        <input
          :id="id"
          v-model="model"
          :type="inputType"
          :placeholder="placeholder"
          :autocomplete="autocomplete"
          :inputmode="inputmode"
          :maxlength="maxlength"
          :disabled="disabled"
          :aria-invalid="error ? 'true' : undefined"
          :aria-describedby="describedBy"
          class="h-full min-w-0 flex-1 bg-transparent text-input outline-none placeholder:text-ink-muted"
        />
        <button
          v-if="type === 'password'"
          type="button"
          class="-mr-2 inline-flex size-touch items-center justify-center rounded-full text-ink-muted active:bg-surface-strong"
          :aria-label="revealed ? fr.ui.hidePassword : fr.ui.showPassword"
          :aria-pressed="revealed"
          @click="revealed = !revealed"
        >
          <Icon :name="revealed ? 'hide' : 'show'" />
        </button>
      </div>
    </template>
  </Field>
</template>
